import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import swal from 'sweetalert';
import Select from 'react-select';
import {
    useCreatUserSeatingProfileMutation,
    useGetSystemSeatsForClientRouteTimeQuery,
} from '../../Features/seatsSlice';
import LoadingBar from 'react-top-loading-bar';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useSelector } from 'react-redux';
import { useIsOnline } from 'react-use-is-online';
import {
    ViewGridIcon,
    CollectionIcon,
    ArrowLeftIcon,
} from '@heroicons/react/outline';

function CreateUserSeatingProfileView() {
    const navigate = useNavigate();
    const user = useSelector(selectCurrentUser);
    const { isOnline } = useIsOnline();

    const [progress, setProgress] = useState(0);

    const [input, setInput] = useState({
        name: '',
    });

    const [seatsArray, setSeats] = useState([]);

    const [errors, setErrors] = useState({
        name: '',
        seats: '',
    });

    const {
        data: seats,
        isLoading,
        isSuccess,
        isError,
        error,
    } = useGetSystemSeatsForClientRouteTimeQuery();

    const [creatSeatingProfile, { isLoading: isCreating }] =
        useCreatUserSeatingProfileMutation();

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSeatsChange = (selectedSeats) => {
        setSeats(selectedSeats || []);

        setErrors((prev) => ({
            ...prev,
            seats: '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setErrors({
            name: '',
            seats: '',
        });

        if (!input.name.trim()) {
            setErrors((prev) => ({
                ...prev,
                name: 'Name is required',
            }));

            return;
        }

        if (!seatsArray || seatsArray.length === 0) {
            setErrors((prev) => ({
                ...prev,
                seats: 'Please select at least one seat',
            }));

            return;
        }

        const data = {
            name: input.name,
            user_id: user.id,
            seats: seatsArray,
        };

        try {
            setProgress(80);

            const resp = await creatSeatingProfile(data).unwrap();

            if (resp?.success === true) {
                setProgress(100);

                swal(
                    'Success',
                    resp?.message || 'Seating profile created successfully',
                    'success'
                );

                navigate(-1);
            } else {
                setProgress(100);
            }

        } catch (err) {
            setProgress(100);

            if (err?.data?.errors) {
                setErrors({
                    name: err.data.errors.name || '',
                    seats: err.data.errors.seats || '',
                });
            } else if (err?.data?.error) {
                swal(
                    'Error',
                    err.data.error,
                    'error'
                );
            } else {
                swal(
                    'Error',
                    'Something went wrong. Please try again.',
                    'error'
                );
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setProgress(100);
        navigate(-1);
    };

    /*
     * react-select needs its own styles so that
     * the component matches the application's
     * light/dark theme and green branding.
     */
    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '42px',
            borderRadius: '8px',
            borderColor: state.isFocused
                ? '#80b643'
                : '#d1d5db',
            boxShadow: state.isFocused
                ? '0 0 0 1px #80b643'
                : 'none',
            backgroundColor: document.documentElement.classList.contains(
                'dark'
            )
                ? '#1f2937'
                : '#ffffff',
            '&:hover': {
                borderColor: '#80b643',
            },
        }),

        menu: (provided) => ({
            ...provided,
            zIndex: 50,
            backgroundColor: document.documentElement.classList.contains(
                'dark'
            )
                ? '#1f2937'
                : '#ffffff',
        }),

        option: (provided, state) => ({
            ...provided,
            fontSize: '14px',
            backgroundColor: state.isSelected
                ? '#80b643'
                : state.isFocused
                    ? 'rgba(128, 182, 67, 0.10)'
                    : 'transparent',
            color: state.isSelected
                ? '#ffffff'
                : document.documentElement.classList.contains('dark')
                    ? '#ffffff'
                    : '#374151',
            cursor: 'pointer',
        }),

        multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'rgba(128, 182, 67, 0.12)',
            borderRadius: '6px',
        }),

        multiValueLabel: (provided) => ({
            ...provided,
            color: '#80b643',
            fontSize: '12px',
            fontWeight: '500',
        }),

        multiValueRemove: (provided) => ({
            ...provided,
            color: '#80b643',
            ':hover': {
                backgroundColor: '#80b643',
                color: '#ffffff',
            },
        }),

        placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af',
            fontSize: '14px',
        }),

        input: (provided) => ({
            ...provided,
            color: document.documentElement.classList.contains('dark')
                ? '#ffffff'
                : '#374151',
            fontSize: '14px',
        }),

        singleValue: (provided) => ({
            ...provided,
            color: document.documentElement.classList.contains('dark')
                ? '#ffffff'
                : '#374151',
            fontSize: '14px',
        }),

        noOptionsMessage: (provided) => ({
            ...provided,
            fontSize: '13px',
            color: '#9ca3af',
        }),
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">

                {/* PAGE HEADER */}
                <div className="flex items-center gap-3 mb-6">

                    <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <ViewGridIcon className="w-6 h-6 text-[#80b643]" />
                    </div>

                    <div>
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            Create Seating Profile
                        </h1>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Create a seating profile for your vehicles
                        </p>
                    </div>

                </div>

                {/* MAIN CARD */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                    {/* CARD HEADER */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">

                        <div className="flex items-center gap-3">

                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <CollectionIcon className="w-5 h-5 text-[#80b643]" />
                            </div>

                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Seating Profile Information
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Enter the profile name and select the seats
                                </p>
                            </div>

                        </div>

                    </div>

                    {/* FORM */}
                    <form
                        onSubmit={handleSubmit}
                        autoComplete="off"
                        className="p-4 sm:p-6"
                    >

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                            {/* NAME */}
                            <div>

                                <label
                                    htmlFor="name"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Profile Name
                                </label>

                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={input.name}
                                    onChange={handleChange}
                                    placeholder="Enter seating profile name"
                                    disabled={isCreating}
                                    className={`w-full px-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-white border ${
                                        errors.name
                                            ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                                            : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]'
                                    } focus:ring-1 outline-none transition disabled:opacity-60`}
                                />

                                {errors.name && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.name}
                                    </p>
                                )}

                            </div>

                            {/* SEATS */}
                            <div>

                                <label
                                    htmlFor="seats"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    Select Seats
                                </label>

                                <Select
                                    inputId="seats"
                                    options={seats || []}
                                    value={seatsArray}
                                    isMulti
                                    isSearchable
                                    isLoading={isLoading}
                                    isDisabled={isCreating}
                                    onChange={handleSeatsChange}
                                    placeholder={
                                        isLoading
                                            ? 'Loading seats...'
                                            : 'Select seats'
                                    }
                                    styles={selectStyles}
                                    noOptionsMessage={() =>
                                        isError
                                            ? 'Unable to load seats'
                                            : 'No seats available'
                                    }
                                />

                                {errors.seats && (
                                    <p className="mt-1.5 text-xs font-medium text-red-500">
                                        {errors.seats}
                                    </p>
                                )}

                            </div>

                        </div>

                        {/* ACTIONS */}
                        <div className="mt-8 pt-5 border-t border-gray-200 dark:border-gray-800">

                            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                                <button
                                    type="button"
                                    onClick={back}
                                    disabled={isCreating}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 w-full sm:w-auto disabled:opacity-50"
                                >
                                    <ArrowLeftIcon className="w-4 h-4" />
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={isCreating}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {isCreating ? (
                                        <>
                                            <svg
                                                className="animate-spin h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                />

                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                />
                                            </svg>

                                            Saving...
                                        </>
                                    ) : (
                                        'Save Profile'
                                    )}
                                </button>

                            </div>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
}

export default CreateUserSeatingProfileView;